import { uniqBy } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { myTaskMeetingsListName } from "src/api/config/meetings.config";
import { myTasksDepartmentsListName, myTasksLocationsListName } from "src/api/config/myTasksLists.config";
import ActiveMeetingsTable from "src/components/tables/meetingTables/ActiveMeetingsTable.mobile";
import { SearchUI } from "src/components/ui/Search";
import { Option, TGSelectPickerByList } from "src/components/ui/TGSelect";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { GetItemsRequestProps } from "src/modules/modules.type";
import { getItems, getItemsSuccess } from "src/modules/spItems/actions";
import { selectAddStatus, selectUpdateStatus } from "src/modules/spItems/selectors";
import { selectCurrentUser } from "src/modules/users/selector";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { getLocalItem, setLocalItem } from "src/utils/localStorage";
import { twMerge } from "tailwind-merge";
import { frequencyList } from "./components/CreateMeetingButton";
import { SearchInputEmployee } from "./components/SearchInputEmployee";
import { MeetingsPageBreadcrumbLayout } from "./MeetingsPageBreadcrumbLayout";
import { ActiveMeetingType, PersonType } from "./type";
import FilterMeetings from "src/components/filter-meetings/filter-meetings";
import FiltersButton from "src/components/filters-button/filters-button";

const MeetingsPageMobile = () => {
  const [activeMeeting, setActiveMeeting] = useState<ActiveMeetingType>(getLocalItem("meetings_active") ?? "active");

  const toggleActive = useCallback((action: ActiveMeetingType) => {
    setActiveMeeting(action);
    setLocalItem("meetings_active", action);
  }, []);

  return (
    <>
      <FlexRow className="px-0 h-auto justify-between items-center shrink-0 mb-[16px]">
        <FlexCol>
          <nav className="flex justify-between w-full" aria-label="Breadcrumb">
            <FlexCol className="grow">
              <ol className="inline-flex items-center">
                <li className="inline-flex items-center" aria-current="page">
                  <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
                    <span>Совещания</span>
                  </div>
                </li>
              </ol>
            </FlexCol>
          </nav>
        </FlexCol>
      </FlexRow>

      {/* <FlexRow className="px-7.5 h-[76px] border-b items-center shrink-0">
        <MeetingsPageBreadcrumbLayout activeMeeting={activeMeeting} changeActive={toggleActive} />
      </FlexRow> */}
      <ActiveMeetings activeMeeting={activeMeeting} />
    </>
  );
};

export default MeetingsPageMobile;

function ActiveMeetings({ activeMeeting }: { activeMeeting: ActiveMeetingType }) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const [pathQuery, setPathQuery] = useSearchParams();

  const locationsResponseData = useGetSelect(myTasksLocationsListName);
  const departmentsResponseData = useGetSelect(myTasksDepartmentsListName);

  const updateStatus = useAppSelector((state) => selectUpdateStatus(state, myTaskMeetingsListName));
  const addStatus = useAppSelector((state) => selectAddStatus(state, myTaskMeetingsListName));

  const getStatus = useAppSelector((state) => selectUpdateStatus(state, myTaskMeetingsListName));
  const [loading, setLoading] = useState(false);

  const [selectedPerson, setSelectedPerson] = useState<PersonType | null | undefined>();
  const [query, setQuery] = useState("");
  const [locations, setLocations] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);
  const [frequencies, setFrequencies] = useState<Option[]>([]);

  const [isOpenFilter, setIsOpenFilter] = useState(false);

  function changeSelectedPerson(person: PersonType | null) {
    setSelectedPerson(person);
  }

  function handleResetPerson() {
    changeSelectedPerson(null);
  }

  function handleResetAllFilters() {
    setLocations([]);
    setDepartments([]);
    setFrequencies([]);
    changeSelectedPerson(null);
  }

  const isSelectedFilters = useMemo(() => {
    const isPersonFilterActive = !!(selectedPerson && selectedPerson.Email);
    return (
      isPersonFilterActive ||
      locations.length > 0 ||
      departments.length > 0 ||
      frequencies.length > 0
    );
  }, [selectedPerson, locations, departments, frequencies]);

  useAsyncEffect(async () => {
    if (currentUser.email && selectedPerson !== undefined) {
      // setLoading(true);
      let requestOwnersBody: GetItemsRequestProps<typeof myTaskMeetingsListName> & { merge: boolean } = {
        listName: myTaskMeetingsListName,
        filters: {
          Status: activeMeeting, // Переключение между "active" и "archived"
        },
        sortOrder: { name: "Created", desc: true },
        merge: true,
      };

      let requestParticipantsBody: GetItemsRequestProps<typeof myTaskMeetingsListName> & { merge: boolean } = {
        listName: myTaskMeetingsListName,
        filters: {
          Status: activeMeeting,
        },
        sortOrder: { name: "Created", desc: true },
        merge: true,
      };

      // Логика для активных элементов

      dispatch(getItemsSuccess({ listName: myTaskMeetingsListName, items: [] }));

      if (activeMeeting === "active") {
        if (!!selectedPerson) {
          if (selectedPerson.Email.toLowerCase() === currentUser.email.toLowerCase()) {
            requestOwnersBody.filters = { ...requestOwnersBody.filters, Owners: [selectedPerson.Email.toLowerCase()] };
            requestParticipantsBody.filters = {
              ...requestParticipantsBody.filters,
              Participants: [selectedPerson.Email.toLowerCase()],
            };
          } else {
            requestOwnersBody.filters = {
              ...requestOwnersBody.filters,
              Owners: [selectedPerson.Email.toLowerCase()],
              isPrivate: false,
            };
            requestParticipantsBody.filters = {
              ...requestParticipantsBody.filters,
              Participants: [selectedPerson.Email.toLowerCase()],
              isPrivate: false,
            };
          }
        } else {
          requestOwnersBody.filters = {
            ...requestOwnersBody.filters,
            isPrivate: false,
          };
          requestParticipantsBody.filters = {
            ...requestParticipantsBody.filters,
            isPrivate: false,
          };
        }
      } else {
        if (selectedPerson) {
          if (selectedPerson.Email.toLowerCase() === currentUser.email.toLowerCase()) {
            requestOwnersBody.filters = { ...requestOwnersBody.filters };
            requestParticipantsBody.filters = {
              ...requestParticipantsBody.filters,
            };
          } else {
            requestOwnersBody.filters = {
              ...requestOwnersBody.filters,
              isPrivate: false,
            };
            requestParticipantsBody.filters = {
              ...requestParticipantsBody.filters,
              isPrivate: false,
            };
          }
        } else {
          requestOwnersBody.filters = {
            ...requestOwnersBody.filters,
            isPrivate: false,
          };
          requestParticipantsBody.filters = {
            ...requestParticipantsBody.filters,
            isPrivate: false,
          };
        }
      }
      // Дополнительные фильтры
      if (query.length) {
        requestOwnersBody.filters = { ...requestOwnersBody.filters, Title: query + "*" };
        requestParticipantsBody.filters = { ...requestParticipantsBody.filters, Title: query + "*" };
      }

      if (frequencies.length) {
        requestOwnersBody.filters = { ...requestOwnersBody.filters, Frequency: frequencies[0].name };
        requestParticipantsBody.filters = {
          ...requestParticipantsBody.filters,
          Frequency: frequencies[0].name,
        };
      }

      if (locations.length) {
        requestOwnersBody.filters = { ...requestOwnersBody.filters, Location: locations[0].id };
        requestParticipantsBody.filters = { ...requestParticipantsBody.filters, Location: locations[0].id };
      }

      if (departments.length) {
        requestOwnersBody.filters = { ...requestOwnersBody.filters, Department: departments[0].id };
        requestParticipantsBody.filters = { ...requestParticipantsBody.filters, Department: departments[0].id };
      }

      try {
        dispatch(getItems(requestOwnersBody));
        dispatch(getItems(requestParticipantsBody));
      } catch (error) {
        console.error(error);
      } finally {
        // setLoading(false);
      }
    }
  }, [
    dispatch,
    activeMeeting,
    selectedPerson,
    frequencies,
    locations,
    departments,
    query,
    addStatus,
    updateStatus,
    getStatus,
  ]);

  useEffect(() => {
    if (currentUser.email) {
      changeSelectedPerson({
        Avatar: currentUser.avatar,
        ShortName: currentUser.shortName,
        Email: currentUser.email,
        Title: currentUser.displayName,
        DisplayName: currentUser.displayName,
        PersonalNumber: Number(currentUser.id),
        ID: currentUser.id,
        Department: "",
        Position: "",
        UserID: currentUser.id,
      } as PersonType);
    }
  }, [currentUser]);

  useEffect(() => {
    dispatch(getItems({ listName: myTasksLocationsListName }));
    dispatch(getItems({ listName: myTasksDepartmentsListName }));
  }, [pathQuery.has("meetingCardMobile")]);

  return (
    <div>
      <div className="mb-5 flex gap-2 items-end no-label">
        <SearchMeetingNameInput query={query} onChange={(value) => setQuery(value)} />
        <FiltersButton isSelectedFilters={isSelectedFilters} onOpenFilters={() => setIsOpenFilter(true)} />
      </div>

      <ActiveMeetingWrapper activeMeeting={activeMeeting} loading={loading} selectedPerson={selectedPerson} />

      <FilterMeetings
        isOpen={isOpenFilter}
        onClose={() => setIsOpenFilter(false)}
        selectedPerson={selectedPerson}
        onChangePerson={changeSelectedPerson}
        locationsResponseData={locationsResponseData}
        locations={locations}
        onSetLocations={setLocations}
        departmentsResponseData={departmentsResponseData}
        departments={departments}
        onSetDepartments={setDepartments}
        frequencyList={frequencyList}
        frequencies={frequencies}
        onSetFrequencies={setFrequencies}
        onResetFilterLocation={() => setLocations([])}
        onResetDepartments={() => setDepartments([])}
        onResetFrequencies={() => setFrequencies([])}
        onResetPerson={handleResetPerson}
        onResetAllFilters={handleResetAllFilters}
        isSelectedFilters={isSelectedFilters}
      />
    </div>
  );
}

function ActiveMeetingWrapper({
  loading,
  activeMeeting,
  selectedPerson,
}: {
  loading: boolean;
  activeMeeting: ActiveMeetingType;
  selectedPerson: PersonType | null | undefined;
}) {
  const { data: myTaskMeetings, status } = useGetSelect(myTaskMeetingsListName);

  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const [activeItems, setActiveItems] = useState<
    (Record<"Title" | "Location" | "Department" | "Role", any> & { ID: number })[]
  >([]);

  useEffect(() => {
    const unique = uniqBy(myTaskMeetings, "ID");

    unique.sort((a, b) => {
      const dateA = moment(a.Created, "DD.MM.YYYY");
      const dateB = moment(b.Created, "DD.MM.YYYY");

      return dateB.diff(dateA);
    });

    let modifiedData =
      activeMeeting === "archive" &&
      !!selectedPerson &&
      selectedPerson.Email.toLowerCase() === currentUser.email.toLowerCase()
        ? unique
            .filter(
              (u) =>
                !u.isPrivate ||
                (selectedPerson &&
                  ((u.Owners && u.Owners.length && u.Owners.includes(selectedPerson.Email)) ||
                    (u.Participants && u.Participants.length && u.Participants.includes(selectedPerson.Email))))
            )
            .map((d) => ({
              ID: d.ID,
              Title: d.Title,
              Location: d.Location.Title || "",
              Department: d.Department.Title || "",
              Role: !selectedPerson
                ? "----"
                : selectedPerson && d.Owners && d.Owners.length && d.Owners.includes(selectedPerson.Email)
                ? "Владелец"
                : selectedPerson &&
                  d.Participants &&
                  d.Participants.length &&
                  d.Participants.includes(selectedPerson.Email)
                ? "Участник"
                : "Не участник",
            }))
        : unique.map((d) => ({
            ID: d.ID,
            Title: d.Title,
            Location: d.Location.Title || "",
            Department: d.Department.Title || "",
            Role: !selectedPerson
              ? "----"
              : selectedPerson && d.Owners && d.Owners.length && d.Owners.includes(selectedPerson.Email)
              ? "Владелец"
              : selectedPerson &&
                d.Participants &&
                d.Participants.length &&
                d.Participants.includes(selectedPerson.Email)
              ? "Участник"
              : "Не участник",
          }));

    setActiveItems(modifiedData);
  }, [myTaskMeetings, selectedPerson, status]);

  return <ActiveMeetingsTable loading={status !== "SUCCESS"} list={activeItems} activeMeeting={activeMeeting} />;
}

function SearchMeetingNameInput({ query, onChange }: { query: string; onChange: (value: string) => void }) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<PersonType[]>([]);

  function resetQuery() {
    setEmployees([]);
    onChange("");
  }
  function handleSearch(value: string) {
    if (!!employees.length && value !== query) {
      // resetQuery(); // Сбросить только если ввод изменился и есть что сбрасывать
    } else {
      onChange(value);
    }
  }

  return (
    <SearchUI
      type="text"
      ref={searchInputRef}
      placeholder={"Найти совещание..."}
      defaultValue={query}
      disabled={false}
      onClick={handleSearch}
      loading={loading}
      label={"Название совещания"}
      resetButton={true}
      inputClass={twMerge("appearance-none")}
      labelClass="font-normal text-black"
      resetHandle={() => resetQuery()}
    />
  );
}
